import { useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import { instance, ensureFreshAccessToken } from 'common/axios/axios';
import { getApiUrl, getGatewayUrl } from 'common/axios/getApiUrl';
import type { LocationSample } from './useEta';
// TODO: [SIMULATOR] — 삭제 시 이 import 와 훅 내부 SIMULATOR 블록 + 파라미터 제거
import {
    SIMULATOR_ENABLED,
    SIMULATOR_TICK_MS,
    simulatorStartPoint,
    simulatorNextStep,
} from 'common/utils/locationSimulator';

export interface MemberLocation {
    latitude: number;
    longitude: number;
    timestamp: number;           // 최신 업데이트 unix ms
    history: LocationSample[];   // 최근 좌표 기록 (최대 10개)
}

export type MemberLocationMap = Record<number, MemberLocation>;

const HISTORY_LIMIT = 10;

/**
 * 모임 참여자의 실시간 위치를 STOMP 으로 구독하는 훅.
 * - 초기: REST `/meetings/{id}/location/current` 로 일괄 로드
 * - 실시간: `/topic/meeting/{id}/location` 구독
 * - 각 userNo 별로 최근 10개 좌표 history 유지 (ETA 계산용)
 *
 * 기존 RealtimeLocation.tsx 와 공존 가능 — 동일 토픽을 복수 구독해도 서버에서 각 연결에 모두 push.
 */
export default function useRealtimeMemberLocations(
    meetingId: string | null | undefined,
    currentUserNo: number | null,
    enabled: boolean = true,
    // TODO: [SIMULATOR] — 삭제 시 이 파라미터 + 아래 SIMULATOR 블록 제거
    simulator?: {
        destination: { latitude: number; longitude: number };
        participants: number[];
        excludeUserNos?: number[];
    },
): MemberLocationMap {
    const [locations, setLocations] = useState<MemberLocationMap>({});
    const stompClientRef = useRef<Client | null>(null);

    useEffect(() => {
        if (!meetingId || !enabled) return;

        let cancelled = false;

        // 1. 초기 위치 일괄 로드
        instance.get(`${getApiUrl('meetingDetail')}/${meetingId}/location/current`)
            .then(res => {
                if (cancelled) return;
                const list = res?.data?.data;
                if (!Array.isArray(list)) return;
                const next: MemberLocationMap = {};
                const now = Date.now();
                list.forEach((loc: any) => {
                    if (loc.latitude && loc.longitude && loc.userNo) {
                        const sample: LocationSample = {
                            latitude: Number(loc.latitude),
                            longitude: Number(loc.longitude),
                            timestamp: loc.timestamp ? Number(loc.timestamp) : now,
                        };
                        next[loc.userNo] = {
                            latitude: sample.latitude,
                            longitude: sample.longitude,
                            timestamp: sample.timestamp,
                            history: [sample],
                        };
                    }
                });
                setLocations(next);
            })
            .catch(() => { /* 실패해도 STOMP 으로 수신 */ });

        // 2. STOMP 구독 — Sec-WebSocket-Protocol 에 bearer.<token> smuggling 으로 토큰 전달
        // 게이트웨이 TokenExtractor 가 추출 → JWT 검증 → X-User-No 주입 → dokocrew-api HandshakeInterceptor 가 세션에 적재
        // beforeConnect 로 매 (재)연결 직전 토큰 fresh 보장 — stale 토큰으로 무한 reconnect 회귀 방지
        const wsUrl = `${getGatewayUrl()}/dokocrew/ws`.replace(/^http/, 'ws');
        let cachedToken: string | null = null;
        const client = new Client({
            webSocketFactory: () => {
                const token = cachedToken || localStorage.getItem('accessToken');
                if (!token) console.warn('[STOMP-mem] 토큰 없음 — 익명 연결 시도');
                return new WebSocket(
                    wsUrl,
                    token ? ['v12.stomp', `bearer.${token}`] : ['v12.stomp'],
                );
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            debug: (str) => console.log('[STOMP-mem]', str),
            beforeConnect: async () => {
                cachedToken = await ensureFreshAccessToken();
            },
            onConnect: () => {
                client.subscribe(
                    `/topic/meeting/${meetingId}/location`,
                    (message) => {
                        try {
                            const update = JSON.parse(message.body);
                            if (!update?.userNo || !update?.latitude || !update?.longitude) return;
                            const sample: LocationSample = {
                                latitude: Number(update.latitude),
                                longitude: Number(update.longitude),
                                timestamp: update.timestamp ? Number(update.timestamp) : Date.now(),
                            };
                            setLocations(prev => {
                                const prevOne = prev[update.userNo];
                                const history = prevOne
                                    ? [...prevOne.history, sample].slice(-HISTORY_LIMIT)
                                    : [sample];
                                return {
                                    ...prev,
                                    [update.userNo]: {
                                        latitude: sample.latitude,
                                        longitude: sample.longitude,
                                        timestamp: sample.timestamp,
                                        history,
                                    },
                                };
                            });
                        } catch (e) {
                            console.warn('[useRealtimeMemberLocations] STOMP 메시지 파싱 실패', e);
                        }
                    }
                    // userNo 헤더는 더 이상 보내지 않음 — 서버가 CONNECT 단계 JWT 에서 추출한 userNo 만 신뢰
                );
            },
        });

        client.activate();
        stompClientRef.current = client;

        return () => {
            cancelled = true;
            if (stompClientRef.current) {
                stompClientRef.current.deactivate();
                stompClientRef.current = null;
            }
        };
    }, [meetingId, currentUserNo, enabled]);

    // ──────────────── TODO: [SIMULATOR] START ────────────────
    // 네이티브 실시간 위치 복구 후 이 블록 전체 삭제
    const simDestLat = simulator?.destination?.latitude;
    const simDestLng = simulator?.destination?.longitude;
    const simParticipantsKey = simulator?.participants?.slice().sort().join(',') || '';
    const simExcludeKey = simulator?.excludeUserNos?.slice().sort().join(',') || '';
    useEffect(() => {
        if (!SIMULATOR_ENABLED) return;
        if (!enabled || !simulator) return;
        if (simDestLat == null || simDestLng == null) return;
        if (!simulator.participants || simulator.participants.length === 0) return;

        const dest = { latitude: simDestLat, longitude: simDestLng };
        const excludeSet = new Set(simulator.excludeUserNos || []);

        // 초기화 — STOMP 로 실제 데이터 아직 안 들어온 userNo 에만 가짜 좌표 주입
        setLocations(prev => {
            const next = { ...prev };
            const now = Date.now();
            simulator.participants.forEach(userNo => {
                if (excludeSet.has(userNo)) return;
                if (next[userNo]) return; // 실제 데이터 우선
                const start = simulatorStartPoint(dest, userNo);
                const sample: LocationSample = { latitude: start.latitude, longitude: start.longitude, timestamp: now };
                next[userNo] = {
                    latitude: start.latitude,
                    longitude: start.longitude,
                    timestamp: now,
                    history: [sample],
                };
            });
            return next;
        });

        const interval = setInterval(() => {
            setLocations(prev => {
                const next = { ...prev };
                const ts = Date.now();
                simulator.participants.forEach(userNo => {
                    if (excludeSet.has(userNo)) return;
                    const current = prev[userNo];
                    if (!current) return;
                    const step = simulatorNextStep(
                        { latitude: current.latitude, longitude: current.longitude },
                        dest,
                    );
                    const sample: LocationSample = {
                        latitude: step.latitude,
                        longitude: step.longitude,
                        timestamp: ts,
                    };
                    next[userNo] = {
                        latitude: step.latitude,
                        longitude: step.longitude,
                        timestamp: ts,
                        history: [...current.history, sample].slice(-HISTORY_LIMIT),
                    };
                });
                return next;
            });
        }, SIMULATOR_TICK_MS);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, simDestLat, simDestLng, simParticipantsKey, simExcludeKey]);
    // ──────────────── TODO: [SIMULATOR] END ────────────────

    return locations;
}
